import { useState } from "react";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  handleSSOLoginRedirection,
  isTMA,
  sourceTelegramApp,
  sourceWebWallet,
  verifyUser,
} from "../constants";
import {
  emailAddress,
  emailAddressPlaceholder,
  invalidEmailAddress,
  loadingMsg,
  LOGIN_PAGE,
  next,
  signInHeader,
  signInSubHeader,
  signUp,
} from "../messages";
import { Input } from "../input/Input";
import BackdropLoader from "../backdropLoader/BackdropLoader";
import Text from "../text/Text";
import InputErrorMessage from "../inputErrorMessage/InputErrorMessage";
import Button from "../button/Button";
import { setLoginData } from "../../redux/actions";
import CommonHeader from "../../components/CommonHeader";
import CommonAuthHeader from "../options/CommonAuthHeader";

const Login = ({ isFromOptions = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loginDetails = useSelector((state) => state.common.loginDetails);

  const validationSchema = object({
    email: string().email(invalidEmailAddress),
  });

  const initialValues = { email: loginDetails?.email || "" };

  const handleLogin = async (val, { resetForm }) => {
    setIsSubmitting(true);

    try {
      const res = await verifyUser({ email: val.email });
      const { signup_method } = res;
      if (signup_method !== "email") {
        const socialMediaLoginRedirection = {
          google: () =>
            handleSSOLoginRedirection({
              authMethod: "google",
              inviteeAuthState: { from: LOGIN_PAGE },
              setIsSubmitting,
              navigate,
              localhostSsoFe: false,
            }),
          apple: () =>
            handleSSOLoginRedirection({
              authMethod: "apple",
              inviteeAuthState: {
                source: isTMA() ? sourceTelegramApp : sourceWebWallet,
                from: LOGIN_PAGE,
              },
              setIsSubmitting,
              navigate,
              localhostSsoFe: false,
            }),
        };
        await socialMediaLoginRedirection?.[signup_method]?.();
      } else {
        dispatch(setLoginData({ ...loginDetails, ...val }));
        setTimeout(() => {
          resetForm(initialValues);
          navigate("/login-step-two");
        }, 0);
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: handleLogin,
    enableReinitialize: true,
  });

  const {
    values,
    touched,
    errors,
    isValid,
    dirty,
    handleChange,
    handleSubmit,
    handleBlur,
  } = formik;

  const disabled =
    loginDetails?.email === values?.email
      ? !isValid || isSubmitting
      : !(isValid && dirty) || isSubmitting;

  const signUpComponent = (
    <Text
      className="blue-text"
      font="bold"
      size={16}
      onClick={() => navigate("/register")}
      sx={{ cursor: "pointer" }}
    >
      {signUp}
    </Text>
  );

  return (
    <>
      <BackdropLoader
        open={isSubmitting}
        alt={loadingMsg}
        text={loadingMsg}
        customClass="loading-in-auth"
      />
      <CommonHeader
        icon="backArrow"
        rightSideComponent={signUpComponent}
        navigation="/"
      />
      <CommonAuthHeader showSpeedLogo={false} sx={{ height: "100%" }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          className="set-elements-vertically"
        >
          <Box>
            <Text className="default-text" size={22} font="bold">
              {signInHeader}
            </Text>
            <Text className="grey-text mt-8" size={14}>
              {signInSubHeader}
            </Text>
            <Input
              customClass="mt-24"
              fullWidth
              showLabel
              label={emailAddress}
              placeholder={emailAddressPlaceholder}
              name="email"
              value={values.email}
              type="email"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
            />
            <InputErrorMessage {...{ touched, errors }} inputName="email" />
          </Box>
          {/* {isFromOptions && authOptionsFooterDivider} */}
          <Box mt="10%">
            <Button
              fullWidth
              sx={!isFromOptions && { m: "14px 20px" }}
              variant="contained"
              label={next}
              disabled={disabled}
              type="submit"
              {...(!isFromOptions && { textClass: "font-14" })}
            />
          </Box>
        </Box>
      </CommonAuthHeader>
    </>
  );
};

export default Login;
